"use client";

import "./fashion-lifestyle.css";
import { useEffect } from "react";

/* =========================================================
   DATA
========================================================= */

const questions = [
  "Which styles are selling—and which are only creating inventory?",
  "Which colour-size combinations will stock out?",
  "Where should inventory be reallocated?",
  "Which stores/channels have the wrong assortment?",
  "What should we replenish and what should we stop buying?",
  "Which collections are growing but diluting margin?",
  "When should markdowns begin?",
  "How much margin are promotions and markdowns consuming?",
  "Which products are becoming slow-moving before the season ends?",
  "What should we buy for the next season?",
];

const intelligenceAreas = [
  {
    title: "Merchandise Intelligence",
    items: "Season • Collection • Style\nColour • Size • Material",
    icon: "hanger",
  },
  {
    title: "Assortment Intelligence",
    items: "Store • Channel • Cluster\nRange • Depth • Localisation",
    icon: "grid",
  },
  {
    title: "Demand & Replenishment",
    items: "Demand • Forecast • Stock-out\nReplenishment • Transfer • What-if",
    icon: "chart",
  },
  {
    title: "Inventory Intelligence",
    items: "Ageing • Slow Movers\nWeeks of Cover • End-of-Season\nInventory Health",
    icon: "box",
  },
  {
    title: "Price & Markdown Intelligence",
    items: "MRP • Realisation • Promotion\nMarkdown • Margin • ROI",
    icon: "tag",
  },
  {
    title: "Customer & Channel Intelligence",
    items: "Stores • E-commerce\nMarketplaces • Customer\nGeography • Loyalty",
    icon: "people",
  },
];

const plmSteps = [
  {
    title: "Consumer Insight",
    description: "Turn shopper trends and preferences into winning style bets.",
    icon: "bulb",
    color: "#1670ED",
  },
  {
    title: "Range & Assortment",
    description: "Plan the right styles, colours and sizes for every store and channel.",
    icon: "box",
    color: "#1670ED",
  },
  {
    title: "Plan & Buy",
    description: "Make smarter buying decisions with real-time demand signals.",
    icon: "cart",
    color: "#1670ED",
  },
  {
    title: "Source & Produce",
    description: "Collaborate with vendors and bring collections to life on time.",
    icon: "factory",
    color: "#1670ED",
  },
  {
    title: "Distribute & Allocate",
    description: "Get the right stock to the right store, fast.",
    icon: "truck",
    color: "#1670ED",
  },
  {
    title: "Sell (Omnichannel)",
    description: "Deliver a seamless shopping experience across every channel.",
    icon: "store",
    color: "#1670ED",
  },
  {
    title: "Customer & Loyalty",
    description: "Turn every purchase into a lasting customer relationship.",
    icon: "people",
    color: "#1670ED",
  },
];

const systems = [
  { name: "SAP", icon: "database" },
  { name: "ERP", icon: "layers" },
  { name: "PLM", icon: "hanger" },
  { name: "Merchandise Planning", icon: "chart" },
  { name: "POS", icon: "store" },
  { name: "WMS", icon: "box" },
  { name: "E-Commerce Marketplaces", icon: "grid" },
  { name: "CRM", icon: "people" },
  { name: "Market Data", icon: "chart" },
];

const timeDecisions = [
  ["Design", "What should we create?", "bulb"],
  ["Buy", "How much should we buy?", "box"],
  ["Launch", "Which channels and markets?", "store"],
  ["Full Price", "Maximise sell-through", "tag"],
  ["Markdown", "What to restock and where?", "chart"],
  ["End of Season", "When and how much?", "grid"],
];

/* =========================================================
   SVG ICON SYSTEM
========================================================= */

const svgProps = {
  viewBox: "0 0 24 24",
  width: "1em",
  height: "1em",
  fill: "none",
  stroke: "currentColor",
  strokeWidth: 1.8,
  strokeLinecap: "round",
  strokeLinejoin: "round",
};

const IconHanger = () => (
  <svg {...svgProps}>
    <path d="M12 5a2.5 2.5 0 1 1 2.5-2.5" />
    <path d="M12 5c0 1.5-1 2-2 2.8L4 12h16l-6-4.2c-1-.7-2-1.3-2-2.8" />
    <path d="M4 12h16" />
  </svg>
);

const IconGrid = () => (
  <svg {...svgProps}>
    <rect x="4" y="4" width="6" height="6" rx="1" />
    <rect x="14" y="4" width="6" height="6" rx="1" />
    <rect x="4" y="14" width="6" height="6" rx="1" />
    <rect x="14" y="14" width="6" height="6" rx="1" />
  </svg>
);

const IconChart = () => (
  <svg {...svgProps}>
    <path d="M4 20V11" />
    <path d="M10 20V6" />
    <path d="M16 20V9" />
    <path d="M22 20V3" />
  </svg>
);

const IconBox = () => (
  <svg {...svgProps}>
    <path d="M21 8l-9-5-9 5 9 5 9-5z" />
    <path d="M3 8v8l9 5 9-5V8" />
    <path d="M12 13v8" />
  </svg>
);

const IconTag = () => (
  <svg {...svgProps}>
    <path d="M12 2h7a1 1 0 0 1 1 1v7a2 2 0 0 1-.6 1.4l-8 8a2 2 0 0 1-2.8 0l-6-6a2 2 0 0 1 0-2.8l8-8A2 2 0 0 1 12 2z" />
    <circle cx="15.5" cy="8.5" r="1.5" fill="currentColor" stroke="none" />
  </svg>
);

const IconPeople = () => (
  <svg {...svgProps}>
    <circle cx="9" cy="8" r="3" />
    <path d="M3 20c0-3.3 2.7-6 6-6s6 2.7 6 6" />
    <circle cx="17" cy="9" r="2.5" />
    <path d="M15.5 14a5 5 0 0 1 5.5 5" />
  </svg>
);

const IconStore = () => (
  <svg {...svgProps}>
    <path d="M3 9l1.5-5h15L21 9" />
    <path d="M4 9v10h16V9" />
    <path d="M4 9a2 2 0 0 0 4 0 2 2 0 0 0 4 0 2 2 0 0 0 4 0 2 2 0 0 0 4 0" />
    <path d="M9 19v-6h6v6" />
  </svg>
);

const IconCart = () => (
  <svg {...svgProps}>
    <circle cx="9" cy="20" r="1" fill="currentColor" stroke="none" />
    <circle cx="18" cy="20" r="1" fill="currentColor" stroke="none" />
    <path d="M2 3h2l2.4 12.2a2 2 0 0 0 2 1.6h8.2a2 2 0 0 0 2-1.6L21 7H6" />
  </svg>
);

const IconFactory = () => (
  <svg {...svgProps}>
    <path d="M3 21V10l6 4v-4l6 4V8l6 4v9H3z" />
    <path d="M7 21v-4M12 21v-4M17 21v-4" />
  </svg>
);

const IconTruck = () => (
  <svg {...svgProps}>
    <rect x="1" y="7" width="13" height="10" rx="1" />
    <path d="M14 10h4l3 3v4h-7z" />
    <circle cx="6" cy="19" r="1.6" />
    <circle cx="17" cy="19" r="1.6" />
  </svg>
);

const IconDatabase = () => (
  <svg {...svgProps}>
    <ellipse cx="12" cy="5" rx="8" ry="3" />
    <path d="M4 5v14c0 1.7 3.6 3 8 3s8-1.3 8-3V5" />
    <path d="M4 12c0 1.7 3.6 3 8 3s8-1.3 8-3" />
  </svg>
);

const IconLayers = () => (
  <svg {...svgProps}>
    <path d="M12 2 2 7l10 5 10-5-10-5z" />
    <path d="M2 12l10 5 10-5" />
    <path d="M2 17l10 5 10-5" />
  </svg>
);

const IconBulb = () => (
  <svg {...svgProps}>
    <path d="M9 18h6M10 22h4" />
    <path d="M12 2a6 6 0 0 0-3.5 10.9c.6.5 1 1.2 1 2.1h5c0-.9.4-1.6 1-2.1A6 6 0 0 0 12 2z" />
  </svg>
);

const IconArrowRight = () => (
  <svg {...svgProps}>
    <path d="M4 12h15" />
    <path d="M13 6l6 6-6 6" />
  </svg>
);

const IconArrowDown = () => (
  <svg {...svgProps}>
    <path d="M12 4v15" />
    <path d="m6 13 6 6 6-6" />
  </svg>
);

const IconShare = () => (
  <svg {...svgProps}>
    <circle cx="18" cy="5" r="2.6" />
    <circle cx="6" cy="12" r="2.6" />
    <circle cx="18" cy="19" r="2.6" />
    <path d="M8.3 10.7l7.4-4.2M8.3 13.3l7.4 4.2" />
  </svg>
);

const IconQuestion = () => (
  <svg {...svgProps}>
    <circle cx="12" cy="12" r="9" />
    <path d="M9.7 9a2.4 2.4 0 1 1 3.9 1.9c-.9.7-1.6 1.1-1.6 2.4" />
    <path d="M12 16.5h.01" />
  </svg>
);

const IconLeaf = () => (
  <svg {...svgProps}>
    <path d="M5 21c8 0 14-6 14-14V4h-3C8 4 3 9 3 17v4z" />
    <path d="M5 21c3-3 6-6 14-14" />
  </svg>
);

const ICONS = {
  hanger: IconHanger,
  grid: IconGrid,
  chart: IconChart,
  box: IconBox,
  tag: IconTag,
  people: IconPeople,
  store: IconStore,
  cart: IconCart,
  factory: IconFactory,
  truck: IconTruck,
  database: IconDatabase,
  layers: IconLayers,
  bulb: IconBulb,
  share: IconShare,
  leaf: IconLeaf,
  arrowRight: IconArrowRight,
  arrowDown: IconArrowDown,
  question: IconQuestion,
};

function Icon({ type, className = "" }) {
  const Component = ICONS[type];
  if (!Component) return null;

  return (
    <span className={`custom-icon ${className}`} aria-hidden="true">
      <Component />
    </span>
  );
}

/* =========================================================
   COMPONENT
========================================================= */

export default function FashionLifestyle() {
  useEffect(() => {
    const elements = document.querySelectorAll(
      ".fashion-page .reveal-item, .fashion-page .reveal-card"
    );

    if (!elements.length) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("in-view");
            observer.unobserve(entry.target);
          }
        });
      },
      {
        threshold: 0.12,
        rootMargin: "0px 0px -30px 0px",
      }
    );

    elements.forEach((element) => observer.observe(element));

    return () => observer.disconnect();
  }, []);

  return (
    <main className="fashion-page">
      {/* =====================================================
          SECTION 01 — HERO
      ===================================================== */}
      <section className="fashion-hero">
        <div className="hero-overlay" />

        <div className="hero-content">
          <div className="hero-left">
            <h1>
              From Collection to Customer.
              <br />
              Know What Will Sell, Where
              <br />
              and at What Margin.
            </h1>

            <p className="hero-description">
              Orane connects product lifecycle, planning, sourcing, inventory,
              channels and customer data to help fashion and lifestyle
              businesses improve sell-through, availability and profitability.
            </p>

            <div className="hero-buttons">
              <a href="/tellusyourproblem#tell-details" className="btn btn-primary">
                Tell Us Your Problem
                <Icon type="arrowRight" />
              </a>

              <a href="#plm" className="btn btn-outline">
                See How It Works
                <span className="play-icon">▶</span>
              </a>
            </div>
          </div>

          <div className="hero-stats">
            <div className="hero-stat">
              <div className="hero-stat-icon">
                <Icon type="hanger" />
              </div>
              <span>
                Right
                <br />
                Products
              </span>
            </div>

            <div className="hero-stat">
              <div className="hero-stat-icon">
                <Icon type="chart" />
              </div>
              <span>
                Higher
                <br />
                Sell-through
              </span>
            </div>

            <div className="hero-stat">
              <div className="hero-stat-icon">
                <Icon type="tag" />
              </div>
              <span>
                Better
                <br />
                Margins
              </span>
            </div>

            <div className="hero-stat">
              <div className="hero-stat-icon">
                <Icon type="box" />
              </div>
              <span>
                Lower
                <br />
                Inventory
              </span>
            </div>

            <div className="hero-stat">
              <div className="hero-stat-icon">
                <Icon type="people" />
              </div>
              <span>
                Happier
                <br />
                Customers
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* =====================================================
          SECTION 02A — QUESTIONS
      ===================================================== */}
      <section className="fashion-section questions-section">
        <div className="fashion-section-inner">
          <div className="questions-card reveal-item">
            <div className="questions-content">
              <div className="section-heading">
                <h2>The Questions Fashion Leaders Ask</h2>
                <p>Real questions. Real decisions. Real impact.</p>
              </div>

              <div className="questions-list">
                {questions.map((question, index) => (
                  <div
                    className="question-item reveal-item"
                    key={question}
                    style={{ "--delay": `${index * 45}ms` }}
                  >
                    <span className="question-number">{index + 1}</span>
                    <p>{question}</p>
                    <span className="question-arrow">
                      <Icon type="arrowRight" />
                    </span>
                  </div>
                ))}
              </div>

              <a href="#contact" className="questions-more">
                View All Questions
                <Icon type="arrowRight" />
              </a>
            </div>

            <div className="questions-image">
              <img src="/assets/images/fashion.png" alt="Fashion & Lifestyle" />
            </div>
          </div>
        </div>
      </section>

      {/* =====================================================
          SECTION 02B — PLM
      ===================================================== */}
      <section className="fashion-section plm-section" id="plm">
        <div className="fashion-section-inner">
          <div className="plm-flow">
            <div className="plm-flow-info reveal-item">
              <div className="section-heading">
                <h2>PLM at the Heart of Everything</h2>
                <p>From idea to impact. A connected product and business lifecycle.</p>
              </div>

              <div className="plm-flow-image">
                <img src="/assets/images/clothes.png" alt="Fashion collection" />

                <div className="plm-flow-badge">
                  <strong>7</strong>
                  <span>Connected Stages</span>
                </div>
              </div>
            </div>

            <div className="plm-flow-steps">
              <div className="plm-flow-line" aria-hidden="true" />

              {plmSteps.map((step, index) => (
                <div
                  className="plm-step reveal-item"
                  key={step.title}
                  style={{
                    "--delay": `${index * 70}ms`,
                    "--step-color": step.color,
                  }}
                >
                  <span className="plm-step-number">
                    {String(index + 1).padStart(2, "0")}
                  </span>

                  <div className="plm-step-icon">
                    <Icon type={step.icon} />
                  </div>

                  <div className="plm-step-text">
                    <h3>{step.title}</h3>
                    <p>{step.description}</p>
                  </div>

                  <div className="plm-step-arrow">
                    <Icon type="arrowRight" />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* =====================================================
          SECTION 02C — INTELLIGENCE
      ===================================================== */}
      <section className="fashion-section intelligence-section">
        <div className="fashion-section-inner">
          <div className="section-heading reveal-item">
            <h2>Key Areas of Intelligence</h2>
            <p>From insight to action. End-to-end decision support.</p>
          </div>

          <div className="intelligence-grid">
            {intelligenceAreas.map((area, index) => (
              <article
                className="intelligence-card reveal-card"
                key={area.title}
                style={{ "--delay": `${index * 70}ms` }}
              >
                <div className="intelligence-icon">
                  <Icon type={area.icon} />
                </div>

                <div>
                  <h3>{area.title}</h3>
                  <ul className="intelligence-points">
                    {area.items
                      .split(/\n|\s*•\s*/)
                      .filter(Boolean)
                      .map((point) => (
                        <li key={point}>{point}</li>
                      ))}
                  </ul>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* =====================================================
          SECTION 03 — WORKED QUESTION
      ===================================================== */}
      <section className="fashion-section worked-section">
        <div className="fashion-section-inner">
          <div className="section-heading reveal-item">
            <h2>A Worked Question</h2>
            <p>
              “Which styles should we replenish—and which should we stop?”
            </p>
          </div>

          <div className="worked-layout">
            <div className="worked-image reveal-item">
              <img src="/assets/images/sellthrough.png" alt="Sell-through analysis" />

              <div className="worked-image-label label-replenish">
                <strong>REPLENISH</strong>
                <span>Drive availability</span>
              </div>

              <div className="worked-image-label label-star">
                <strong>STAR</strong>
                <span>Scale up</span>
              </div>

              <div className="worked-image-label label-stop">
                <strong>STOP / MARKDOWN</strong>
                <span>Release inventory</span>
              </div>

              <div className="worked-image-label label-watch">
                <strong>WATCH</strong>
                <span>Monitor closely</span>
              </div>
            </div>

            <div className="investigate-card reveal-item">
              <span className="mini-label">INVESTIGATE BY</span>

              <div className="investigate-list">
                <span><Icon type="hanger" /> Style</span>
                <span><Icon type="grid" /> Colour</span>
                <span><Icon type="box" /> Size</span>
                <span><Icon type="store" /> Store / Channel</span>
                <span><Icon type="grid" /> Region</span>
                <span><Icon type="people" /> Customer</span>
                <span><Icon type="chart" /> Season</span>
                <span><Icon type="tag" /> Price / Markdown</span>
              </div>

              <div className="decision-divider" />

              <span className="mini-label">THEN DECIDE</span>

              <div className="decision-list">
                <span>✓ Replenish</span>
                <span>✓ Transfer</span>
                <span>✓ Markdown</span>
                <span>✓ Stop Buying</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* =====================================================
          SECTION 04 — TIME BUSINESS
      ===================================================== */}
      <section className="fashion-section time-section">
        <div className="fashion-section-inner">
          <div className="section-heading reveal-item">
            <h2>
              Fashion is a Time Business
            </h2>
            <p>The value of the decision falls as time passes.</p>
          </div>

          <div className="time-layout">
            <div className="time-line">
              {timeDecisions.map(([title, text, icon], index) => (
                <div
                  className="time-item reveal-item"
                  key={title}
                  style={{ "--delay": `${index * 65}ms` }}
                >
                  <span className="time-number">
                    {String(index + 1).padStart(2, "0")}
                  </span>

                  <div className="time-icon">
                    <Icon type={icon} />
                  </div>

                  <div className="time-copy">
                    <strong>{title}</strong>
                    <span>{text}</span>
                  </div>

                  {index !== timeDecisions.length - 1 && (
                    <div className="time-arrow">
                      <Icon type="arrowDown" />
                    </div>
                  )}
                </div>
              ))}
            </div>

            <div className="decision-value reveal-item">
              <img
                src="/assets/images/Fashion%20&%20Lifestyle.png"
                alt="Fashion rack"
                className="decision-value-image"
              />

              <div className="decision-value-overlay">
                <span className="decision-value-bar" aria-hidden="true" />
                <p>
                  Value of the decision
                  <br />
                  falls <span>as time passes</span>
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* =====================================================
          SECTION 05 — ENTERPRISE SYSTEMS + CTA
      ===================================================== */}
      <section className="fashion-section enterprise-section">
        <div className="enterprise-layout">
          <div className="systems-card reveal-item">
            <div className="section-heading">
              <h2>
                Enterprise Systems <span>& Data</span>
              </h2>
              <p>
                Bring data together. Add fashion context. Deliver intelligence.
              </p>
            </div>

            <div className="systems-grid">
              {systems.map((system, index) => (
                <div
                  className={`system-box reveal-card ${
                    index === 0 ? "system-box-primary" : ""
                  }`}
                  key={system.name}
                  style={{ "--delay": `${index * 45}ms` }}
                >
                  <Icon type={system.icon} />
                  <span className="system-box-label">
                    <strong>{system.name}</strong>
                  </span>
                </div>
              ))}
            </div>

            <div className="data-boxes">
              <div className="reveal-item">
                <Icon type="database" />
                <div className="data-box-text">
                  <strong>Trusted Fashion Data</strong>
                  <span>
                    Product | Customer | Channel | Transactional | External
                  </span>
                </div>
              </div>

              <div className="reveal-item">
                <Icon type="layers" />
                <div className="data-box-text">
                  <strong>Fashion Semantics & Context</strong>
                  <span>
                    Season | Collection | Style | Material | Channel | Market
                  </span>
                </div>
              </div>

              <div className="reveal-item">
                <Icon type="bulb" />
                <div className="data-box-text">
                  <strong>Fashion Intelligence</strong>
                  <span>Analytics | AI | Scenarios | Recommendations</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="final-cta" id="contact">
          <div className="final-cta-inner">
            <div className="final-cta-text">
              <h2>
                Fashion Moves Fast.
                <br />
                Your Decisions Have To Move Faster.
              </h2>

              <a href="/tellusyourproblem#tell-details" className="btn btn-primary">
                Tell Us Your Problem
                <Icon type="arrowRight" />
              </a>
            </div>

            <div className="final-cta-stats">
              <div className="final-cta-stat">
                <div className="final-cta-stat-icon">
                  <Icon type="hanger" />
                </div>
                <span>
                  Right
                  <br />
                  Products
                </span>
              </div>

              <div className="final-cta-stat">
                <div className="final-cta-stat-icon">
                  <Icon type="chart" />
                </div>
                <span>
                  Higher
                  <br />
                  Sell-through
                </span>
              </div>

              <div className="final-cta-stat">
                <div className="final-cta-stat-icon">
                  <Icon type="tag" />
                </div>
                <span>
                  Better
                  <br />
                  Margins
                </span>
              </div>

              <div className="final-cta-stat">
                <div className="final-cta-stat-icon">
                  <Icon type="box" />
                </div>
                <span>
                  Lower
                  <br />
                  Inventory
                </span>
              </div>

              <div className="final-cta-stat">
                <div className="final-cta-stat-icon">
                  <Icon type="people" />
                </div>
                <span>
                  Happier
                  <br />
                  Customers
                </span>
              </div>

              <div className="final-cta-stat">
                <div className="final-cta-stat-icon">
                  <Icon type="leaf" />
                </div>
                <span>
                  A More Sustainable
                  <br />
                  Tomorrow
                </span>
              </div>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}
